import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://gourav.blog';
const DEFAULT_IMAGE = `${SITE_URL}/gourav-transparent.png`;
const BRAND_KEYWORDS = [
  'Gourav',
  'Gourav Boiri',
  'Boiri Gourav',
  'Gourav portfolio',
  'gourav.blog',
  'Gourav blog',
  'Boiri',
  'software developer',
  'freelance developer',
  'freelance software developer',
  'freelance web developer',
  'mobile app developer',
  'websites and apps',
  'React developer',
  'React Native developer',
  'Next.js developer',
  'full stack developer',
  'Netflix portfolio',
  'Netflix theme portfolio',
  'Netflix inspired portfolio',
];

type SeoConfig = {
  title: string;
  description: string;
  canonicalPath: string;
  keywords?: string[];
  index?: boolean;
};

const seoByPath: Record<string, SeoConfig> = {
  '/': {
    title: 'Gourav | Freelance Software & Web Developer Portfolio',
    description:
      'Gourav Boiri is a freelance software, web, and mobile app developer building websites, apps, and Netflix-inspired portfolio experiences with React, React Native, Next.js, and full stack development.',
    canonicalPath: '/',
    keywords: ['gourav', 'gourav boiri', 'gourav portfolio', 'gourav.blog', 'netflix theme portfolio gourav'],
  },
  '/browse': {
    title: 'Gourav Portfolio | Browse Netflix-Style Portfolio',
    description:
      'Browse Gourav Boiri Netflix-style portfolio for software projects, professional experience, skills, recommendations, blogs, and contact details.',
    canonicalPath: '/browse',
    keywords: ['gourav portfolio', 'netflix portfolio', 'netflix inspired portfolio', 'software developer portfolio'],
  },
  '/work-experience': {
    title: 'Work Experience | Gourav Boiri Software Developer',
    description:
      'Explore Gourav Boiri professional software engineering experience across web, mobile, cloud, and full stack product development.',
    canonicalPath: '/work-experience',
    keywords: ['gourav boiri experience', 'software developer experience', 'full stack developer experience'],
  },
  '/recommendations': {
    title: 'Recommendations | Gourav Boiri',
    description:
      'Read professional recommendations and feedback for Gourav Boiri freelance software, web, and mobile app development work.',
    canonicalPath: '/recommendations',
    keywords: ['gourav recommendations', 'freelance developer reviews', 'software developer recommendations'],
  },
  '/skills': {
    title: 'Skills | Gourav Boiri Freelance Developer',
    description:
      'Review Gourav Boiri technical skills in React, React Native, Next.js, TypeScript, backend development, cloud, and app delivery.',
    canonicalPath: '/skills',
    keywords: ['gourav skills', 'React developer', 'React Native developer', 'Next.js developer', 'websites and apps'],
  },
  '/projects': {
    title: 'Projects | Gourav Boiri Websites and Apps',
    description:
      'See selected web, mobile, and full stack projects built by Gourav Boiri using modern software engineering tools and frameworks.',
    canonicalPath: '/projects',
    keywords: ['gourav projects', 'software developer projects', 'websites and apps', 'freelance developer portfolio'],
  },
  '/contact-me': {
    title: 'Hire Gourav Boiri | Freelance Software Developer',
    description:
      'Contact Gourav Boiri to discuss freelance web development, mobile app development, React Native, Next.js, and full stack projects.',
    canonicalPath: '/contact-me',
    keywords: ['hire Gourav', 'hire Gourav Boiri', 'freelance developer', 'freelance software developer', 'websites and apps'],
  },
  '/music': {
    title: 'Music | Gourav Boiri',
    description: 'Explore the music section of Gourav Boiri Netflix-inspired portfolio.',
    canonicalPath: '/music',
    keywords: ['Gourav music', 'Gourav Boiri personal portfolio'],
  },
  '/photography': {
    title: 'Photography | Gourav Boiri',
    description: 'Explore photography and visual highlights from Gourav Boiri portfolio.',
    canonicalPath: '/photography',
    keywords: ['Gourav photography', 'Gourav Boiri personal portfolio'],
  },
  '/blogs': {
    title: 'Blogs | Gourav Boiri Software Development',
    description: 'Read software development blogs from Gourav Boiri on AI, Firebase, Bootstrap, Tailwind CSS, and modern web development.',
    canonicalPath: '/blogs',
    keywords: ['Gourav blog', 'gourav blogs', 'software development blog', 'Gourav Boiri blog'],
  },
  '/certifications': {
    title: 'Certifications | Gourav Boiri Developer',
    description: 'View certifications and learning achievements from Gourav Boiri software development journey.',
    canonicalPath: '/certifications',
    keywords: ['Gourav certifications', 'software developer certifications', 'Gourav Boiri developer'],
  },
};

const legacyCanonicalPaths: Record<string, string> = {
  '/experience': '/work-experience',
  '/contact': '/contact-me',
};

function normalizePath(pathname: string) {
  if (pathname.length > 1 && pathname.endsWith('/')) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

function getSeoConfig(pathname: string): SeoConfig {
  const normalizedPath = normalizePath(pathname);

  if (seoByPath[normalizedPath]) {
    return seoByPath[normalizedPath];
  }

  if (legacyCanonicalPaths[normalizedPath]) {
    const canonicalPath = legacyCanonicalPaths[normalizedPath];
    return {
      ...seoByPath[canonicalPath],
      canonicalPath,
      index: false,
    };
  }

  if (normalizedPath.startsWith('/profile/')) {
    const profileName = normalizedPath.replace('/profile/', '');
    const readableProfileName = profileName
      .split('-')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');

    return {
      title: `${readableProfileName || 'Profile'} Profile | Gourav Boiri`,
      description:
        'Explore a personalized Netflix-style view of Gourav Boiri portfolio, projects, skills, experience, blogs, and contact information.',
      canonicalPath: normalizedPath,
      keywords: ['Gourav profile', 'Gourav portfolio profile', 'Netflix theme portfolio Gourav'],
    };
  }

  return {
    title: 'Page Not Found | Gourav Boiri',
    description: 'The requested page could not be found on Gourav Boiri portfolio.',
    canonicalPath: normalizedPath,
    index: false,
  };
}

function setMetaAttribute(attribute: 'name' | 'property', key: string, content: string) {
  let meta = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, key);
    document.head.appendChild(meta);
  }

  meta.setAttribute('content', content);
}

function setCanonical(canonicalUrl: string) {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');

  if (!canonical) {
    canonical = document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    document.head.appendChild(canonical);
  }

  canonical.setAttribute('href', canonicalUrl);
}

function setJsonLd(seo: SeoConfig, canonicalUrl: string) {
  let script = document.head.querySelector<HTMLScriptElement>('script[data-seo-json-ld="true"]');

  if (!script) {
    script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo-json-ld', 'true');
    document.head.appendChild(script);
  }

  script.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Person',
        '@id': `${SITE_URL}/#person`,
        name: 'Gourav Boiri',
        alternateName: ['Gourav', 'Boiri Gourav', 'Boiri', 'Gourav Blog', 'Gourav Portfolio'],
        url: SITE_URL,
        image: DEFAULT_IMAGE,
        jobTitle: 'Freelance Software Developer',
        description:
          'Gourav Boiri is a freelance software, web, and mobile app developer building websites, apps, and full stack products.',
        knowsAbout: [
          'Software Development',
          'Web Development',
          'Mobile App Development',
          'React',
          'React Native',
          'Next.js',
          'TypeScript',
          'Full Stack Development',
          'Freelance Development',
        ],
        sameAs: ['https://github.com/njrgourav11', 'https://www.linkedin.com/in/bgourav2287'],
      },
      {
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: 'Gourav Blog',
        alternateName: ['Gourav Portfolio', 'gourav.blog', 'Gourav Boiri Portfolio', 'Netflix Theme Portfolio Gourav'],
        url: SITE_URL,
        publisher: { '@id': `${SITE_URL}/#person` },
        inLanguage: 'en',
      },
      {
        '@type': 'ProfessionalService',
        '@id': `${SITE_URL}/#service`,
        name: 'Gourav Boiri Freelance Software Development',
        url: SITE_URL,
        image: DEFAULT_IMAGE,
        telephone: '+917606914711',
        priceRange: '$$',
        founder: { '@id': `${SITE_URL}/#person` },
        areaServed: 'Worldwide',
        serviceType: ['Website Development', 'Mobile App Development', 'Full Stack Development', 'React Development'],
        description:
          'Freelance software development services for websites, mobile apps, React, React Native, Next.js, and full stack products.',
      },
      {
        '@type': 'WebPage',
        '@id': `${canonicalUrl}#webpage`,
        url: canonicalUrl,
        name: seo.title,
        description: seo.description,
        isPartOf: { '@id': `${SITE_URL}/#website` },
        about: { '@id': `${SITE_URL}/#person` },
        primaryImageOfPage: {
          '@type': 'ImageObject',
          url: DEFAULT_IMAGE,
        },
        inLanguage: 'en',
      },
    ],
  });
}

const Seo = () => {
  const location = useLocation();

  useEffect(() => {
    const seo = getSeoConfig(location.pathname);
    const canonicalUrl = `${SITE_URL}${seo.canonicalPath}`;
    const robotsContent = seo.index === false ? 'noindex, follow' : 'index, follow';

    document.title = seo.title;
    setCanonical(canonicalUrl);
    setMetaAttribute('name', 'description', seo.description);
    setMetaAttribute('name', 'keywords', Array.from(new Set([...BRAND_KEYWORDS, ...(seo.keywords || [])])).join(', '));
    setMetaAttribute('name', 'robots', robotsContent);
    setMetaAttribute('property', 'og:title', seo.title);
    setMetaAttribute('property', 'og:description', seo.description);
    setMetaAttribute('property', 'og:url', canonicalUrl);
    setMetaAttribute('property', 'og:image', DEFAULT_IMAGE);
    setMetaAttribute('property', 'og:site_name', 'Gourav Blog');
    setMetaAttribute('name', 'twitter:title', seo.title);
    setMetaAttribute('name', 'twitter:description', seo.description);
    setMetaAttribute('name', 'twitter:image', DEFAULT_IMAGE);
    setJsonLd(seo, canonicalUrl);
  }, [location.pathname]);

  return null;
};

export default Seo;
